import { isGrpcRuntimeAvailable } from '../../../shared/api/grpc/transport';
import { pipeexRuntimeConfig } from '../../../shared/config/pipeexRuntimeConfig';
import { getE2eBootstrapConfig } from '../../../shared/config/e2eBootstrap';
import { useConfigurationStore } from '../../../shared/stores/useConfigurationStore';

const E2E_BOOTSTRAP =
  typeof window === 'undefined'
    ? null
    : getE2eBootstrapConfig(window.location.search, process.env.NODE_ENV);

export const USE_E2E_VALIDATION_MOCK = E2E_BOOTSTRAP?.validationMock === true;

interface E2EValidationQuickFixPayload {
  parameterName: string;
  revitParameterName: string;
  value: string;
  elementIds: string[];
}

export function applyE2EValidationQuickFix(payload: E2EValidationQuickFixPayload) {
  console.info('[validation-e2e] quick-fix', JSON.stringify(payload));
  return {
    totalCount: payload.elementIds.length,
    fixedCount: payload.elementIds.length,
    failedCount: 0,
    results: payload.elementIds.map((elementId) => ({
      elementId,
      status: 'fixed',
      message: `Set to '${payload.value}'`,
      appliedValue: payload.value,
    })),
  };
}

/** Keeps mock validation in flight long enough for Playwright to exercise busy-state races. */
export async function waitForE2EValidationMock<T>(result: T): Promise<T> {
  if (E2E_BOOTSTRAP?.validationMockDelay) {
    const delayMilliseconds = E2E_BOOTSTRAP.validationMockContextChange ? 6000 : 3000;
    await new Promise((resolve) => setTimeout(resolve, delayMilliseconds));
  }

  if (E2E_BOOTSTRAP?.validationMockContextChange) {
    const configuration = useConfigurationStore.getState();
    configuration.updateMenuConfiguration({
      pipingSpecValue: `${configuration.menuConfiguration.pipingSpecValue}-e2e-changed`,
    });
  }

  return result;
}

// gRPC is the only Revit transport (the WebView2 COM bridge has been removed),
// so in the plugin USE_GRPC is simply "is the gRPC runtime available". This makes
// the validation reads call the gRPC client functions directly instead of routing
// through the legacy host bridge.
export const USE_GRPC = isGrpcRuntimeAvailable() && !USE_E2E_VALIDATION_MOCK;

// Use mock data only when no real runtime is available, unless a dev/e2e validation
// bootstrap explicitly forces the mock path for visual QA or regression checks.
export const USE_MOCK =
  USE_E2E_VALIDATION_MOCK ||
  (pipeexRuntimeConfig.useMockData && !isGrpcRuntimeAvailable());

export type ValidationRuntimeMode = 'grpc' | 'mock' | 'host';

export function getValidationRuntimeMode(): ValidationRuntimeMode {
  if (USE_GRPC) {
    return 'grpc';
  }
  if (USE_MOCK) {
    return 'mock';
  }
  return 'host';
}

export function isValidationMockMode(): boolean {
  return USE_MOCK;
}

export function isValidationGrpcMode(): boolean {
  return USE_GRPC;
}

export function isE2EValidationMock(): boolean {
  return USE_E2E_VALIDATION_MOCK;
}
